import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getShares,
  createShare as createShareRequest,
  updateShare as updateShareRequest,
  setShareEnabled,
  getShareFormMetadata,
} from '../services/SharesService';
import { Share, ShareFilters, ShareFormMetadata, ShareInput } from '../types/interfaces';

export const useShares = (initialFilters: ShareFilters = {}) => {
  const [shares, setShares] = useState<Share[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isToggling, setIsToggling] = useState<boolean>(false);
  const [filters, setFilters] = useState<ShareFilters>(initialFilters);
  const [formMetadata, setFormMetadata] = useState<ShareFormMetadata | null>(null);
  const requestIdRef = useRef<number>(0);

  const fetchShares = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const response = await getShares(filters);
      if (requestIdRef.current !== requestId) return;
      if (response.success && response.data) {
        setShares(response.data);
      } else {
        setError(response.message || 'Error al cargar las acciones');
      }
    } catch {
      if (requestIdRef.current !== requestId) return;
      setError('Error al cargar las acciones');
    } finally {
      if (requestIdRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [filters]);

  useEffect(() => {
    fetchShares();
  }, [fetchShares]);

  useEffect(() => {
    getShareFormMetadata().then((response) => {
      if (response.success && response.data) {
        setFormMetadata(response.data);
      }
    });
  }, []);

  const refresh = useCallback(() => fetchShares(), [fetchShares]);

  const createShare = useCallback(
    async (input: ShareInput): Promise<{ success: boolean; message?: string; status?: number }> => {
      setIsSaving(true);
      setError(null);
      try {
        const response = await createShareRequest(input);
        if (response.success) {
          await refresh();
          return { success: true };
        }
        const errorMsg = response.message || 'Error al crear la acción';
        setError(errorMsg);
        return { success: false, message: errorMsg, status: response.status };
      } catch {
        const errorMsg = 'Error al crear la acción';
        setError(errorMsg);
        return { success: false, message: errorMsg };
      } finally {
        setIsSaving(false);
      }
    },
    [refresh],
  );

  const updateShare = useCallback(
    async (
      id: number,
      input: ShareInput,
    ): Promise<{ success: boolean; message?: string; status?: number }> => {
      setIsSaving(true);
      setError(null);
      try {
        const response = await updateShareRequest(id, input);
        if (response.success) {
          await refresh();
          return { success: true };
        }
        const errorMsg = response.message || 'Error al actualizar la acción';
        setError(errorMsg);
        return { success: false, message: errorMsg, status: response.status };
      } catch {
        const errorMsg = 'Error al actualizar la acción';
        setError(errorMsg);
        return { success: false, message: errorMsg };
      } finally {
        setIsSaving(false);
      }
    },
    [refresh],
  );

  const toggleEnabled = useCallback(
    async (
      id: number,
      enabled: boolean,
    ): Promise<{ success: boolean; message?: string; status?: number }> => {
      setIsToggling(true);
      setError(null);
      try {
        const response = await setShareEnabled(id, enabled);
        if (response.success) {
          await refresh();
          return { success: true };
        }
        const errorMsg = response.message || 'Error al cambiar el estado de la acción';
        setError(errorMsg);
        return { success: false, message: errorMsg, status: response.status };
      } catch {
        const errorMsg = 'Error al cambiar el estado de la acción';
        setError(errorMsg);
        return { success: false, message: errorMsg };
      } finally {
        setIsToggling(false);
      }
    },
    [refresh],
  );

  return {
    shares,
    isLoading,
    error,
    isSaving,
    isToggling,
    filters,
    setFilters,
    refresh,
    createShare,
    updateShare,
    toggleEnabled,
    formMetadata,
  };
};
